import type { Feature } from "geojson";
import * as L from "leaflet";
import { useEffect, useRef, useState } from "react";

import { removeAllDrawnLayers } from "src/utils/map.utils";

export function useDrawPolygon(
	map: L.Map,
	options: { color?: string; fillColor?: string; fillOpacity?: number } = {},
) {
	const [polygon, setPolygon] = useState<Feature | null>(null);
	const {
		color = "#3388ff",
		fillColor = "#3388ff",
		fillOpacity = 0.2,
	} = options;

	useEffect(() => {
		return function cleanup() {
			stopEditDrawing();
			removeAllDrawings();
		};
	}, []);

	function renderInitialPolygon(initialPolygon: Feature | Feature[]) {
		L.geoJSON(initialPolygon, {
			style: {
				color,
				fillColor,
				fillOpacity,
			},
			pane: "drawing-pane",
		})
			.addTo(map)
			.on("pm:edit", (event) => {
				// @ts-expect-error: incomplete type
				setPolygon(event.layer.toGeoJSON());
			});
		if (Array.isArray(initialPolygon)) return;
		setPolygon(initialPolygon);
	}

	function stopDrawing() {
		map.pm.disableDraw("Polygon");
	}

	function startDrawing() {
		map.pm.setGlobalOptions({
			panes: {
				vertexPane: "drawing-pane-vertex",
				layerPane: "drawing-pane",
				markerPane: "drawing-pane",
			},
		});
		map.pm.enableDraw("Polygon", {
			finishOn: "dblclick",
			pathOptions: {
				color,
				fillColor,
				fillOpacity,
				pane: "drawing-pane",
			},
			templineStyle: {
				pane: "drawing-pane-vertex",
				color,
			},
			hintlineStyle: {
				pane: "drawing-pane-vertex",
				color,
				dashArray: [5, 5],
			},
		});

		map.on("pm:create", (event) => {
			const { layer } = event;

			// @ts-expect-error: incomplete type
			setPolygon(layer.toGeoJSON());
			map.pm.disableDraw("Polygon");
			layer.on("pm:edit", (event: L.LeafletEvent) => {
				const { layer } = event;
				setPolygon(layer.toGeoJSON());
			});
		});

		return function cleanup() {
			map.off("pm:create");
		};
	}

	function redrawDrawing() {
		setPolygon(null);
		removeAllDrawings();

		setTimeout(() => {
			startDrawing();
		}, 100);
	}

	function editDrawing() {
		map.pm.setGlobalOptions({
			limitMarkersToCount: 100,
			panes: {
				vertexPane: "drawing-pane-vertex",
				layerPane: "drawing-pane",
				markerPane: "drawing-pane",
			},
		});
		map.pm.enableGlobalEditMode({});
	}

	function stopEditDrawing() {
		map.pm.disableGlobalEditMode();
	}

	function removeAllDrawings() {
		removeAllDrawnLayers(map);
	}

	return {
		polygon,
		renderInitialPolygon,
		setPolygon,
		startDrawing,
		stopDrawing,
		editDrawing,
		redrawDrawing,
		stopEditDrawing,
		removeAllDrawings,
	};
}

export function useDrawPolygons(
	map: L.Map,
	options: { color?: string; fillColor?: string; fillOpacity?: number } = {},
) {
	const [polygons, setPolygons] = useState<Feature[] | null>(null);
	const geojsonRef = useRef<L.GeoJSON | null>(null);

	const {
		color = "#3388ff",
		fillColor = "#3388ff",
		fillOpacity = 0.2,
	} = options;

	useEffect(() => {
		return function cleanup() {
			stopEditDrawing();
			removeAllDrawings();
		};
	}, []);

	function syncPolygonsFromGroup() {
		if (!geojsonRef.current) return;
		const features = geojsonRef.current.getLayers().flatMap((layer) => {
			// @ts-expect-error: incomplete type
			return layer.toGeoJSON();
		});

		setPolygons(features as Feature[]);
	}

	function renderInitialPolygons(initialPolygons: Feature[]) {
		removeAllDrawings();

		const geojson = L.geoJSON(initialPolygons, {
			style: {
				color,
				fillColor,
				fillOpacity,
			},
			pane: "drawing-pane",
		})
			.addTo(map)
			.on("pm:edit", syncPolygonsFromGroup);

		geojsonRef.current = geojson;
		setPolygons(initialPolygons);
	}

	function stopDrawing() {
		map.pm.disableDraw("Polygon");
	}

	function startDrawing() {
		map.pm.setGlobalOptions({
			panes: {
				vertexPane: "drawing-pane-vertex",
				layerPane: "drawing-pane",
				markerPane: "drawing-pane",
			},
		});

		map.pm.enableDraw("Polygon", {
			finishOn: "dblclick",
			pathOptions: {
				color,
				fillColor,
				fillOpacity,
				pane: "drawing-pane",
			},
			templineStyle: {
				pane: "drawing-pane-vertex",
				color,
			},
			hintlineStyle: {
				pane: "drawing-pane-vertex",
				color,
				dashArray: [5, 5],
			},
		});

		map.on("pm:create", (event) => {
			const { layer } = event;

			// @ts-expect-error: incomplete type
			setPolygons([layer.toGeoJSON()]);
			map.pm.disableDraw("Polygon");
			layer.on("pm:edit", (event: L.LeafletEvent) => {
				const { layer } = event;
				setPolygons([layer.toGeoJSON()]);
			});
		});

		return function cleanup() {
			map.off("pm:create");
		};
	}

	function redrawDrawing() {
		setPolygons(null);
		removeAllDrawings();

		setTimeout(() => {
			startDrawing();
		}, 100);
	}

	function editDrawing() {
		map.pm.setGlobalOptions({
			limitMarkersToCount: 100,
			panes: {
				vertexPane: "drawing-pane-vertex",
				layerPane: "drawing-pane",
				markerPane: "drawing-pane",
			},
		});

		map.pm.enableGlobalEditMode({});
	}

	function stopEditDrawing() {
		map.pm.disableGlobalEditMode();
	}

	function removeAllDrawings() {
		geojsonRef.current = null;
		removeAllDrawnLayers(map);
	}

	return {
		polygons,
		renderInitialPolygons,
		setPolygons,
		startDrawing,
		stopDrawing,
		editDrawing,
		redrawDrawing,
		stopEditDrawing,
		removeAllDrawings,
	};
}
